import { IS_PRODUCTION } from "./environment"
import { sessionStorage } from "./storage"

declare global {
  interface Window {
    dataLayer: object[]
  }
}

const GA4_PREFIX = "ga4_"

type EventParams = {
  [key: string]: string | number | boolean | object
}

type EcommerceParams = EventParams & {
  currency?: string
  value?: number
  items: EcommerceItem[]
  optionalParams?: EventParams
}

type Event = EventParams & {
  event: string
}

type EcommerceEvent = EcommerceParams & {
  event: string
}

type GA4Event = (Event | EcommerceEvent) & {
  event_type: "GA4Event"
}

type StringifiedEvent = string

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#purchase_item
type EcommerceItem = {
  coupon?: string
  discount?: number
  index?: number
  item_brand?: string
  item_category: string
  item_category2?: string
  item_category3?: string
  item_category5?: string
  item_id?: string
  item_list_name?: string
  item_name: string
  item_variant?: string
  price: number
  quantity: number
}

interface ErrorShownParams {
  eventGroup: string
  errorText: string
  errorId?: string
  payload?: Record<string, unknown>
}

const tryParseJSON = (json: Event | StringifiedEvent): object => {
  try {
    return JSON.parse(<StringifiedEvent>json)
  } catch {
    return <object>json
  }
}

const pushGa4Event = (event: Event | StringifiedEvent): void => {
  const payload = <GA4Event>{
    ...tryParseJSON(event),
    event_type: "GA4Event"
  }
  window.dataLayer = window.dataLayer || []

  // As recommended per docs, nullify any previous `ecommerce` value before pushing to avoid
  // potentially conflicting ecommerce data due to recursive merges.
  if (payload.ecommerce) {
    window.dataLayer.push({ ecommerce: null })
  }

  window.dataLayer.push(payload)

  if (!IS_PRODUCTION) {
    console.log("[GA4 Event]", payload) // eslint-disable-line no-console
  }

  // Nullify `event_type` to prevent recursive merge issues on successive data layer
  // pushes from other sources. E.g. a third-party push to the data layer could
  // inadvertently trigger GA4 tags in GTM if this param is still present.
  window.dataLayer.push({ event_type: null })
}

const saveGa4ToStorage = ([key, value]: [string, string]): void => {
  sessionStorage?.setItem(`${GA4_PREFIX}${key}`, value)
}

const pushGa4StorageToDataLayer = (): void => {
  if (!sessionStorage) {
    return
  }

  window.dataLayer = window.dataLayer || []

  for (let i = 0, size = sessionStorage.length; i < size; i++) {
    const key = sessionStorage.key(i)

    if (key?.startsWith(GA4_PREFIX)) {
      window.dataLayer.push({ [key.slice(GA4_PREFIX.length)]: sessionStorage.getItem(key) })
      sessionStorage.removeItem(key)
    }
  }
}

const saveNavigationTypeToStorage = (type: string): void => {
  saveGa4ToStorage(["navigation_type", type])
}

const pushEcommerceEvent = (ecommerce: EcommerceEvent): void => {
  pushGa4Event({
    event: ecommerce.event,
    ecommerce: {
      items: ecommerce.items,
      currency: "GBP",
      value: ecommerce.value,
      ...ecommerce.optionalParams
    }
  })
}

const addToCartEvent = (item: EcommerceItem, value: number): void => {
  pushEcommerceEvent({ event: "add_to_cart", items: [item], value })
}

const removeFromCartEvent = (item: EcommerceItem, value: number): void => {
  pushEcommerceEvent({ event: "remove_from_cart", items: [item], value })
}

const carouselClickEvent = (eventGroup: string, clickTarget: string): void => {
  pushGa4Event({
    event: "carousel_click",
    event_group: eventGroup,
    click_target: clickTarget
  })
}

const errorShownEvent = ({
  eventGroup,
  errorText,
  errorId = "",
  payload = {}
}: ErrorShownParams): void => {
  const payloadParams = Object.entries(payload).map(([key, val]) => `${key}: ${val}`)
  const allParams = [`message: ${errorText}`, `error_id: ${errorId || null}`, ...payloadParams]

  pushGa4Event({
    event: "error_shown",
    event_group: eventGroup,
    page_location: window.location.href,
    ...Object.fromEntries(allParams.map((param, index) => [`ce_param${index + 1}`, param]))
  })
}

// Calculate ecommerce `value` using price, quantity, and discount. Use when `value` itself is
// not present in payload.
const computedValue = ({ discount, price, quantity }: EcommerceItem): number => {
  return quantity * (price - (discount || 0))
}

const selectOrClearContent = (
  event: "select_content" | "clear_content",
  contentType: string,
  itemName: string,
  listName?: string
): void => {
  pushGa4Event({
    event,
    content_type: contentType,
    item_name: itemName,
    ...(listName ? { item_list_name: listName } : {})
  })
}

const selectContentEvent = (contentType: string, itemName: string, listName?: string): void => {
  selectOrClearContent("select_content", contentType, itemName, listName)
}

const clearContentEvent = (contentType: string, itemName: string, listName?: string): void => {
  selectOrClearContent("clear_content", contentType, itemName, listName)
}

const selectItemEvent = (item: EcommerceItem, value?: number): void => {
  pushEcommerceEvent({ event: "select_item", items: [item], value: value || computedValue(item) })
}

const viewItemEvent = (item: EcommerceItem, value?: number): void => {
  pushEcommerceEvent({ event: "view_item", items: [item], value: value || computedValue(item) })
}

const viewItemListEvent = (listName: string, items: EcommerceItem[]): void => {
  pushEcommerceEvent({ event: "view_item_list", item_list_name: listName, items })
}

const virtualPageViewEvent = (title: string, path: string): void => {
  const currentUrl = window.location.protocol + "//" + window.location.host

  pushGa4Event({
    event: "virtual_page_view",
    page_title: title,
    page_location: currentUrl + path
  })
}

const addEventAttributeListener = (): void => {
  // Regular click events are handled via GTM because they have well-defined event
  // parameters, but other custom events can have any number (and name) of params so we
  // have to parse and transform them here.
  // For any such event to be recorded in GA4 we must ensure ONE of the following:
  // 1. All custom params are added to the "Custom Event" tag in GTM, or
  // 2. A tag/trigger combo is created in GTM specifically for the event
  document.addEventListener("click", (e) => {
    const ga4Element = <HTMLElement>(e.target as HTMLElement).closest("[data-ga4-event]")
    if (!ga4Element) return

    const eventParams = Object.entries(ga4Element.dataset).reduce((prev, [attr, val]) => {
      const isGa4Attribute = attr.slice(0, 3) === "ga4"

      if (isGa4Attribute && val) {
        const snakeCaseParam = attr
          .slice(3)
          .split(/(?=[A-Z])/)
          .join("_")
          .toLowerCase()

        return { ...prev, [snakeCaseParam]: tryParseJSON(val) }
      } else {
        return prev
      }
    }, {})

    pushGa4Event(<Event>eventParams)
  })
}

const addStorageAttributeListener = (): void => {
  document.addEventListener("click", (e) => {
    const ga4Element = <HTMLElement>(e.target as HTMLElement).closest("[data-ga4-storage]")
    const data = ga4Element?.dataset["ga4Storage"]
    if (!data) return

    // Attribute is of the form: `data-ga4-storage="key:value"`
    saveGa4ToStorage(data.split(":") as [string, string])
  })
}

const setupGa4 = (): void => {
  addEventAttributeListener()
  addStorageAttributeListener()
  pushGa4StorageToDataLayer()
}

const runGa4TemplateScript = (template: HTMLTemplateElement): void => {
  const script = document.createElement("script")
  script.textContent = template.content.querySelector("script")!.textContent

  document.body.appendChild(script)
}

export {
  type EcommerceEvent,
  type EcommerceItem,
  type GA4Event,
  addToCartEvent,
  carouselClickEvent,
  clearContentEvent,
  errorShownEvent,
  pushGa4Event,
  pushGa4StorageToDataLayer,
  removeFromCartEvent,
  runGa4TemplateScript,
  saveNavigationTypeToStorage,
  selectContentEvent,
  selectItemEvent,
  setupGa4,
  viewItemEvent,
  viewItemListEvent,
  virtualPageViewEvent
}
